/* Hand-off prefill for the hosted form (/?prefill=…).
 *
 * A traffic-driving partner can send applicants to the form with whatever
 * details they already hold; the applicant starts at Step 1 with those
 * answers filled in, reviews everything, and completes the journey normally.
 *
 * Accepted encodings (single `prefill` query parameter):
 *   - URI-encoded JSON:      ?prefill=%7B%22firstName%22%3A%22Jane%22%7D
 *   - base64url(JSON, UTF-8): ?prefill=eyJmaXJzdE5hbWUiOiJKYW5lIn0
 *
 * Safety rules, enforced here:
 *   - Only real form fields are accepted (whitelisted against INITIAL).
 *   - Consents, declarations and internal integration fields can never be
 *     preset by a link — those are the applicant's own actions.
 *   - A returning applicant's saved answers always beat link values.
 */
import { INITIAL } from "../data/data.js";

/* Applicant-only declarations/consents and internal integration fields. */
const BLOCKED = new Set([
  "consentCreditCheck", "consentPrivacyPolicy", "consentDataExchange",
  "consentCreditGuide", "consentComms", "contactOptIn",
  "repaymentAwareness", "repaymentIssueDetails",
  "illionReference", "illionConfirmedBy", "bankStatementsUrl", "lenderReference",
]);

/* Decode the raw ?prefill= value. Returns a plain object or null. */
export function decodePrefill(raw) {
  if (!raw || typeof raw !== "string" || raw.length > 6000) return null;
  let json = raw.trim();
  if (json[0] !== "{") {
    try {
      const b64 = json.replace(/-/g, "+").replace(/_/g, "/");
      const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
      json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    } catch (e) { return null; }
  }
  try {
    const obj = JSON.parse(json);
    return obj && typeof obj === "object" && !Array.isArray(obj) ? obj : null;
  } catch (e) { return null; }
}

/* Keep only known, permitted fields; drop anything the applicant has already
   answered differently in their saved draft (their input wins). */
export function sanitizePrefill(obj, draft) {
  if (!obj) return undefined;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!(k in INITIAL) || k.startsWith("_") || BLOCKED.has(k)) continue;
    if (v == null) continue;
    const t = typeof v;
    if (t !== "string" && t !== "number" && t !== "boolean" && t !== "object") continue;
    if (draft && k in draft && JSON.stringify(draft[k]) !== JSON.stringify(INITIAL[k])) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}
